import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import { AuthProvider } from '@/context/AuthContext';
import { CurrencyContextProvider } from '@/hooks/useCurrencyContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { BulkImportProvider } from '@/context/BulkImportContext';
import { SingleImportProvider } from '@/context/SingleImportContext';
import { Toaster } from '@/components/ui/sonner';
import RootErrorBoundary from '@/components/RootErrorBoundary';
import ProtectedRoute from '@/components/ProtectedRoute';
import AppShell from '@/components/AppShell';
import { ConfirmDialogProvider } from '@/components/ConfirmDialog';

import Login from '@/pages/Login';
import Signup from '@/pages/Signup';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import Onboarding from '@/pages/Onboarding';
import OnboardingComplete from '@/pages/OnboardingComplete';
import AuthCallback from '@/pages/AuthCallback';
import Dashboard from '@/pages/Dashboard';
import DashboardRoute from '@/pages/DashboardRoute';
import Transactions from '@/pages/Transactions';
import CSVImport from '@/pages/CSVImport';
import ImportHub from '@/pages/ImportHub';
import Insights from '@/pages/Insights';
import Settings from '@/pages/Settings';
import SettingsBilling from '@/pages/SettingsBilling';
import SettingsFamily from '@/pages/SettingsFamily';
import SettingsLifeProfile from '@/pages/SettingsLifeProfile';
import Help from '@/pages/Help';

import Welcome from '@/pages/Welcome';
import AcceptInvite from '@/pages/AcceptInvite';
import AcceptBetaInvite from '@/pages/AcceptBetaInvite';
// Phase 84.22.2 — NetWorth page retired; /net-worth now redirects to /accounts.
// Import removed to keep the App bundle lean; the page file remains in the
// codebase (pages/NetWorth.js) but is no longer routed.
import Accounts from '@/pages/Accounts';
import CategoryDetail from '@/pages/CategoryDetail';
import AccountDetail from '@/pages/AccountDetail';
import Pricing from '@/pages/Pricing';
import Terms from '@/pages/Terms';
import Privacy from '@/pages/Privacy';
import Refunds from '@/pages/Refunds';
import Cookies from '@/pages/Cookies';
import AcceptableUse from '@/pages/AcceptableUse';
import CookieConsentBanner from '@/components/CookieConsentBanner';
import Goals from '@/pages/Goals';
import Reflections from '@/pages/Reflections';
import Brief from '@/pages/Brief';
import Monthly from '@/pages/Monthly';
import Profile from '@/pages/Profile';
import ProfileHub from '@/pages/ProfileHub';
// Phase 82.13 — legacy `pages/Recurring.js` and the `RecurringManageDeprecated`
// shim have been removed. `/recurring/manage` now redirects inline via
// <Navigate> (see the route below), so any long-lived bookmarks or email
// links still land somewhere sensible.
import RecurringPage from '@/pages/RecurringPage';
import Liabilities from '@/pages/Liabilities';
import Projections from '@/pages/Projections';
import FI from '@/pages/FI';
import PlanAhead from '@/pages/PlanAhead';
import RecurringAutoRun from '@/components/RecurringAutoRun';
import GatedRoute from '@/components/GatedRoute';
import WelcomeTour from '@/components/WelcomeTour';

// Phase 68.B / perf — SuperAdmin is the largest single page in the app (820+
// lines, 18 tab components, ~50KB of JS gzipped). Code-splitting it means
// customer users never download admin code, and the bundle parsed on first
// paint of /dashboard shrinks accordingly.
const SuperAdmin = lazy(() => import('@/pages/SuperAdmin'));
// Phase 70J — review tray is lazy too; only loaded if the user clicks
// the post-remap banner CTA.
const ReviewUncategorized = lazy(() => import('@/pages/ReviewUncategorized'));

/* Tiny full-screen fallback used by Suspense boundaries while the lazy
   chunk is being fetched. Deliberately minimal so it doesn''t flash. */
const RouteFallback = () => (
  <div
    aria-busy="true"
    aria-live="polite"
    style={{
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'var(--muted-foreground, #6B7280)',
      fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
      fontSize: 13,
    }}
    data-testid="route-suspense-fallback"
  >
    Loading…
  </div>
);

const Shell = ({ children }) => (
  <AppShell>
    <RecurringAutoRun />
    {/* Phase 55 follow-up #6 — one-time onboarding tour. Renders nothing
        when the user's profile has welcome_tour_completed_at set. Lives
        inside Shell so it shows on the first post-auth page they hit. */}
    <WelcomeTour />
    {children}
  </AppShell>
);

export default function App() {
  return (
    // RootErrorBoundary is the outermost wrapper so it can catch failures in
    // any provider (Theme, Auth, ConfirmDialog) and in the router itself.
    // See components/RootErrorBoundary.js for the fallback UI + rationale.
    <RootErrorBoundary>
    <ThemeProvider>
      <AuthProvider>
        <CurrencyContextProvider>
        <BulkImportProvider>
        <SingleImportProvider>
        <ConfirmDialogProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/accept-invite" element={<AcceptBetaInvite />} />
            <Route path="/onboarding/complete" element={<ProtectedRoute requireOnboarding={false}><OnboardingComplete /></ProtectedRoute>} />
            <Route path="/onboarding" element={<ProtectedRoute requireOnboarding={false}><Onboarding /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><Shell><DashboardRoute /></Shell></ProtectedRoute>} />
            <Route path="/transactions" element={<ProtectedRoute><Shell><Transactions /></Shell></ProtectedRoute>} />
            <Route path="/transactions/category/:slug" element={<ProtectedRoute><Shell><CategoryDetail /></Shell></ProtectedRoute>} />
            <Route path="/review-uncategorized" element={<ProtectedRoute><Shell><ReviewUncategorized /></Shell></ProtectedRoute>} />
            <Route path="/goals" element={<Navigate to="/plan-ahead?tab=goals" replace />} />
            <Route path="/reflections" element={<ProtectedRoute><Shell><Reflections /></Shell></ProtectedRoute>} />
            <Route path="/brief" element={<ProtectedRoute><Shell><Brief /></Shell></ProtectedRoute>} />
            <Route path="/monthly" element={<ProtectedRoute><Shell><Monthly /></Shell></ProtectedRoute>} />
            {/* v3.7.7 Phase 84.31 — Unified Profile hub replaces the old
                split between the /profile Financial DNA drilldown and the
                /settings/life-profile catalog. The DNA page moves to
                /financial-dna; deep-links from `/settings?section=profile`
                or `/settings?section=life-profile` still resolve because
                the Settings sidebar item now navigates externally to
                /profile (see Settings.js). */}
            <Route path="/profile" element={<ProtectedRoute><Shell><ProfileHub /></Shell></ProtectedRoute>} />
            <Route path="/financial-dna" element={<ProtectedRoute><Shell><Profile /></Shell></ProtectedRoute>} />
            <Route path="/recurring" element={<ProtectedRoute><Shell><RecurringPage /></Shell></ProtectedRoute>} />
            {/* Phase 82.12 — legacy /recurring/manage is deprecated. The
                shim below shows a one-time toast and redirects to /recurring.
                Kept for one release cycle so bookmarks resolve, then Phase
                82.13 will delete it along with pages/Recurring.js. */}
            <Route path="/recurring/manage" element={<Navigate to="/recurring" replace />} />
            <Route path="/accounts/recurring" element={<Navigate to="/recurring" replace />} />
            <Route path="/wealth" element={<Navigate to="/accounts?tab=investments" replace />} />
            <Route path="/liabilities" element={<Navigate to="/accounts?tab=liabilities" replace />} />
            {/* Phase 84.22.2 — /net-worth is retired; its content lives on
                Accounts > Overview now. Redirect keeps old bookmarks alive.
                Phase 84.22.8 — the standalone /pages/NetWorth.js file has
                been deleted as part of the Wealth-page retire pass. */}
            <Route path="/net-worth" element={<Navigate to="/accounts" replace />} />
            <Route path="/accounts" element={<ProtectedRoute><Shell><GatedRoute featureKey="accounts"><Accounts /></GatedRoute></Shell></ProtectedRoute>} />
            <Route path="/accounts/:id/detail" element={<ProtectedRoute><Shell><GatedRoute featureKey="accounts"><AccountDetail /></GatedRoute></Shell></ProtectedRoute>} />
            <Route path="/projections" element={<Navigate to="/plan-ahead?tab=projections" replace />} />
            <Route path="/fi" element={<ProtectedRoute><Shell><GatedRoute featureKey="fi_tracker"><FI /></GatedRoute></Shell></ProtectedRoute>} />
            <Route path="/plan-ahead" element={<ProtectedRoute><Shell><PlanAhead /></Shell></ProtectedRoute>} />
            {/* Phase 84.2 — Adaptive import landing. Users can drop any
                file at /import and we auto-detect content class + route
                them to the right downstream flow. Legacy direct-entry
                URL /import/statements keeps working for anyone who
                bookmarks it. Investments quickjump lands on the
                Investments tab of /accounts (the Wealth page is retired
                as of Phase 84.22.8). */}
            <Route path="/import" element={<ProtectedRoute><Shell><ImportHub /></Shell></ProtectedRoute>} />
            <Route path="/import/statements" element={<ProtectedRoute><Shell><CSVImport /></Shell></ProtectedRoute>} />
            <Route path="/import/investments" element={<Navigate to="/accounts?tab=investments" replace />} />
            <Route path="/insights" element={<ProtectedRoute><Shell><Insights /></Shell></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Shell><Settings /></Shell></ProtectedRoute>} />
            <Route path="/settings/billing" element={<ProtectedRoute><Shell><SettingsBilling /></Shell></ProtectedRoute>} />
            <Route path="/settings/family"  element={<ProtectedRoute><Shell><SettingsFamily /></Shell></ProtectedRoute>} />
            <Route path="/settings/life-profile" element={<Navigate to="/profile?tab=life" replace />} />
            {/* Phase 84.15 \u2014 in-product Help Center. Deep-links:
                /help, /help/:categorySlug, /help/:categorySlug/:articleSlug. */}
            <Route path="/help" element={<ProtectedRoute><Shell><Help /></Shell></ProtectedRoute>} />
            <Route path="/help/:categorySlug" element={<ProtectedRoute><Shell><Help /></Shell></ProtectedRoute>} />
            <Route path="/help/:categorySlug/:articleSlug" element={<ProtectedRoute><Shell><Help /></Shell></ProtectedRoute>} />
            <Route path="/welcome" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />
            <Route path="/invite"           element={<ProtectedRoute><AcceptInvite /></ProtectedRoute>} />
            <Route path="/invite/:token"    element={<ProtectedRoute><AcceptInvite /></ProtectedRoute>} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/terms"           element={<Terms />} />
            <Route path="/privacy"         element={<Privacy />} />
            <Route path="/refunds"         element={<Refunds />} />
            <Route path="/cookies"         element={<Cookies />} />
            <Route path="/acceptable-use"  element={<AcceptableUse />} />
            {/* Phase 19, Internal Superadmin console. Bare ProtectedRoute (no Shell)
                and no sidebar entry; further gated by useIsSuperadmin() inside the page.
                Phase 68.B perf: lazy-loaded so the customer bundle never ships admin code. */}
            <Route path="/sadmin" element={
              <ProtectedRoute>
                <Suspense fallback={<RouteFallback />}>
                  <SuperAdmin />
                </Suspense>
              </ProtectedRoute>
            } />
            {/* Carry the query string across the redirect — `/?demo=1` is the
                documented way in and dropping the search made it a no-op. */}
            <Route path="/" element={<Navigate to={{ pathname: '/dashboard', search: window.location.search }} replace />} />
            <Route path="*" element={<Navigate to={{ pathname: '/dashboard', search: window.location.search }} replace />} />
          </Routes>
          <Toaster richColors position="bottom-right" />
          {/* Phase 38.7, Global cookie consent. Self-hides once the user
              dismisses with either Accept or Decline; persists choice in
              localStorage under `2120_cookie_consent`. */}
          <CookieConsentBanner />
        </BrowserRouter>
        </ConfirmDialogProvider>
        </SingleImportProvider>
        </BulkImportProvider>
        </CurrencyContextProvider>
      </AuthProvider>
    </ThemeProvider>
    </RootErrorBoundary>
  );
}
